import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormControl, UntypedFormGroup, type AbstractControl } from '@angular/forms';
import { Store } from '@ngrx/store';
import { AppState } from '@reducers/interfaces';
import { type ContractTypeModel } from '@reducers/orm/contract-type/contract-type.model';
import { getActiveContractTypes } from '@reducers/orm/contract-type/contract-type.service';
import { canViewContracts } from '@reducers/orm/contract/contract.service';
import { type PermissionGroupModel } from '@reducers/orm/permission-group/permission-group.model';
import { getPermissionGroups } from '@reducers/orm/permission-group/permission-group.service';
import { canViewSkills, getSkillsMultiSelect } from '@reducers/orm/skill/skill.service';
import { filterPersistenceDebounceTime, PageFiltersService } from '@reducers/page-filters/page-filters.service';
import { PlanType } from '@shiftbase-com/ts-shared/models';
import { combineLatest } from 'rxjs';
import { debounceTime, switchMap, take } from 'rxjs/operators';

import { lazySelect } from '../../../../shared/lazy-select.observable';
import { SidebarComponent } from '../../../shared/sidebar/sidebar.component';
import { canViewInactiveUsers, canViewPermissions, getEmployeeSidebarFilters } from '../../employee-list.helper';

@Component({
  standalone: false,
  selector: 'employee-sidebar',
  templateUrl: './employee-sidebar.component.html',
})
export class EmployeeSidebarComponent implements OnInit {
  private sidebar = inject(SidebarComponent);
  private filterService = inject(PageFiltersService);
  private store = inject<Store<AppState>>(Store);
  private destroyRef = inject(DestroyRef);

  filters: ReturnType<typeof getEmployeeSidebarFilters> | undefined;
  canViewContracts$ = this.store.select(canViewContracts);
  canViewPermissions$ = this.store.select(canViewPermissions);
  canViewSkills$ = this.store.select(canViewSkills);
  canViewInactiveUsers$ = this.store.select(canViewInactiveUsers);

  checkboxesFormGroup = new UntypedFormGroup({});

  skills = this.store.selectSignal(getSkillsMultiSelect);
  planType = PlanType;

  permissionGroups: PermissionGroupModel[] = [];
  contractTypes: ContractTypeModel[] = [];

  multiSelectConfig = {
    hasAllCheckBox: true,
    hasFilter: false,
    hasCollapseExpand: false,
    maxHeight: '100%',
  };

  ngOnInit() {
    combineLatest([this.store.select(getActiveContractTypes), this.store.select(getPermissionGroups)])
      .pipe(
        switchMap(([contractTypes, permissionGroups]) => {
          this.filters = undefined;
          this.contractTypes = contractTypes;
          this.permissionGroups = permissionGroups;

          return this.store.pipe(lazySelect(getEmployeeSidebarFilters));
        }),
        take(1),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((filters) => {
        Object.entries(filters).forEach(([groupKey, groupFilters]) => {
          // `skills` holds a list of selected ids; every other group is a map of toggles.
          if (Array.isArray(groupFilters)) {
            this.checkboxesFormGroup.addControl(groupKey, new UntypedFormControl(groupFilters));
            return;
          }

          const groupControl = new UntypedFormGroup({});

          Object.entries(groupFilters).forEach(([filterKey, isSelected]) => {
            groupControl.addControl(filterKey, new UntypedFormControl(isSelected));
          });

          this.checkboxesFormGroup.addControl(groupKey, groupControl);
        });

        this.filters = filters;
      });

    this.checkboxesFormGroup.valueChanges
      .pipe(debounceTime(filterPersistenceDebounceTime), takeUntilDestroyed(this.destroyRef))
      .subscribe((formValue) => {
        this.filterService.setFilters('employee', formValue);
      });
  }

  toggle(groupKey: string) {
    const group: UntypedFormGroup = <UntypedFormGroup>this.checkboxesFormGroup.get(groupKey);
    if (!group || !group.controls) {
      return;
    }
    const controls = Object.values(group.controls);
    if (!controls || controls.length === 0) {
      return;
    }
    const currentVal = controls[0].value;
    controls.forEach((control: UntypedFormControl) => {
      control.setValue(!currentVal);
    });
  }

  close() {
    this.sidebar.close();
  }

  asFormGroup(control: AbstractControl): UntypedFormGroup {
    return control as UntypedFormGroup;
  }
}
